import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Link, useSearchParams } from "react-router";
import { Trans, msg, useLingui } from "@lingui/macro";
import type { MessageDescriptor } from "@lingui/core";
import Input from "@app/components/Input";
import { useLocaleContext } from "@app/contexts/LocaleContext";
import API from "@app/services/api";
import {
  specialityLabel,
  type SpecialityDescriptor,
} from "@app/utils/speciality";

/** Value the backend accepts beside both lists, for a speciality or a software it does not list. */
const OTHER = "autre";

/**
 * The backend's `statut` values: physicians, and the institutions and companies that now ask too.
 * They are stored as they are, so a value is added, never renamed. What the form asks follows from
 * it — the backend drops whatever a statut is not asked for:
 *
 * - a physician: a title (Dr, Pr) and a speciality, both required;
 * - a hospital, a learned society or a company: a contact with a job title instead, the speciality
 *   optional;
 * - a learned society or a company: no patient record software either.
 */
type Statut =
  | "Médecin"
  | "Chirurgien"
  | "Hôpital"
  | "Société savante"
  | "Fabricant de dispositifs médicaux"
  | "Industriel de santé";

const PHYSICIAN_STATUTS: readonly Statut[] = ["Médecin", "Chirurgien"];

/** A company in the health industry: a device manufacturer, a pharmaceutical, biotech or healthtech one. */
const INDUSTRY_STATUTS: readonly Statut[] = [
  "Fabricant de dispositifs médicaux",
  "Industriel de santé",
];

/** A physician's title; an institution's or a company's contact gives none. */
type Titre = "Dr" | "Pr";

/** The backend's offer ids, and what the website's links preselect (`offre=atlas`). */
type Offre = "atlas" | "alliance" | "lab" | "on-demand";

/**
 * The MAGE-X offers, one per audience, in the catalogue's order. Ticking one records an interest —
 * optional, several allowed, none meaning "not decided yet" — never a subscription nor a consent to
 * share anything. Product names are not translated; their audience is.
 */
const OFFERS: readonly {
  id: Offre;
  name: string;
  audience: MessageDescriptor;
}[] = [
  {
    id: "atlas",
    name: "MAGE-X Atlas",
    audience: msg`Hôpitaux et sociétés savantes`,
  },
  {
    id: "alliance",
    name: "MAGE-X Alliance",
    audience: msg`Médecins libéraux qui contribuent`,
  },
  {
    id: "lab",
    name: "MAGE-X Lab",
    audience: msg`Médecins libéraux en autonomie`,
  },
  {
    id: "on-demand",
    name: "MAGE-X On-Demand",
    audience: msg`Industriels de la santé`,
  },
];

/**
 * The offers a link preselects: `offre=atlas`, the parameter repeated or comma-separated. An unknown
 * value is ignored rather than refused — an outdated link must still open the form.
 */
function offersFromUrl(searchParams: URLSearchParams): Offre[] {
  const asked = new Set(
    searchParams
      .getAll("offre")
      .flatMap((value) => value.split(","))
      .map((value) => value.trim().toLowerCase()),
  );
  return OFFERS.filter((offer) => asked.has(offer.id)).map((offer) => offer.id);
}

type SoftwareVendor = { id: string; label: string };

type AccessRequestOptions = {
  specialites: SpecialityDescriptor[];
  editeurs_logiciels: SoftwareVendor[];
  booking_url: string | null;
};

type ApiAnswer = {
  ok?: boolean;
  message?: string;
  error?: string;
  data?: Partial<AccessRequestOptions> | null;
};

const fieldClass =
  "mb-4 w-full rounded border border-primary border-opacity-30 px-2 py-2 focus:border-primary focus:ring-2 focus:ring-primary focus:ring-opacity-30 focus:outline-none bg-white text-slate-900";

/**
 * An access request, from a physician, a hospital, a learned society or a health industry company —
 * the one form behind the welcome page's dialog and the website's "Subscribe" button (which links to
 * `/welcome?acces=1`, with `&offre=atlas` to preselect an offer). Two steps: the request itself,
 * stored and announced to the team as soon as it is sent, then the first call booked in the team's
 * calendar. The second step has no way out but booking: access is opened at the end of that call.
 */
export default function AccessRequestForm({
  initialEmail = "",
  onAccountExists,
}: {
  initialEmail?: string;
  /** The address already has an account: the caller switches to the login tab. */
  onAccountExists?: (email: string) => void;
}) {
  const { t } = useLingui();
  const [searchParams] = useSearchParams();

  const [titre, setTitre] = useState<Titre>("Dr");
  const [statut, setStatut] = useState<Statut>("Médecin");
  const [fonction, setFonction] = useState("");
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState(initialEmail);
  const [telephone, setTelephone] = useState("");
  const [clinique, setClinique] = useState("");
  const [specialite, setSpecialite] = useState("");
  const [editeur, setEditeur] = useState("");
  const [offres, setOffres] = useState<Offre[]>(() =>
    offersFromUrl(searchParams),
  );
  const [consent, setConsent] = useState(false);

  const isPhysician = PHYSICIAN_STATUTS.includes(statut);
  const isIndustry = INDUSTRY_STATUTS.includes(statut);
  const isLearnedSociety = statut === "Société savante";
  // A learned society or a company runs no patient record software: nothing to connect to.
  const asksSoftware = !isIndustry && !isLearnedSociety;

  // Kept in the catalogue's order, whatever order the boxes are ticked in.
  const toggleOffre = (id: Offre, checked: boolean) =>
    setOffres((current) =>
      OFFERS.map((offer) => offer.id).filter((offer) =>
        offer === id ? checked : current.includes(offer),
      ),
    );

  const [options, setOptions] = useState<AccessRequestOptions>({
    specialites: [],
    editeurs_logiciels: [],
    booking_url: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<"exists" | "failed" | null>(null);

  useEffect(() => {
    let cancelled = false;
    API.get({ path: "/user/request-access/options" }).then(
      (response: ApiAnswer) => {
        if (cancelled || !response?.ok || !response.data) return;
        setOptions({
          specialites: response.data.specialites ?? [],
          editeurs_logiciels: response.data.editeurs_logiciels ?? [],
          booking_url: response.data.booking_url ?? null,
        });
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  // The website tags its links; the request keeps where it came from.
  const utm = useMemo(
    () => ({
      utm_source: searchParams.get("utm_source")?.trim() || undefined,
      utm_campaign: searchParams.get("utm_campaign")?.trim() || undefined,
    }),
    [searchParams],
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const username = (e.target as HTMLFormElement).username.value;
    setSubmitting(true);
    setError(null);
    API.post({
      path: "/user/request-access",
      body: {
        // Fields hidden for this statut keep what was typed before switching: not sent.
        titre: isPhysician ? titre : null,
        statut,
        fonction: isPhysician ? null : fonction || null,
        prenom,
        nom,
        email,
        telephone,
        clinique,
        specialite: specialite || null,
        editeur_logiciel: asksSoftware ? editeur || null : null,
        offres,
        consent_accepted: consent,
        username,
        ...utm,
      },
    })
      .then((response: ApiAnswer) => {
        if (response.error === "user_already_exists") {
          setError("exists");
        } else if (response.ok) {
          setSent(true);
        } else {
          setError("failed");
        }
      })
      .finally(() => setSubmitting(false));
  };

  if (sent && options.booking_url) {
    return <BookingStep url={options.booking_url} />;
  }

  if (sent) {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-primary"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <p className="text-sm font-semibold text-slate-900">
          <Trans>Demande envoyée</Trans>
        </p>
        <p className="text-xs text-slate-500 max-w-xs">
          <Trans>
            Merci. L'équipe MAGE-X examine votre demande et vous répond par
            email.
          </Trans>
        </p>
      </div>
    );
  }

  return (
    <form className="flex flex-col" autoComplete="on" onSubmit={handleSubmit}>
      {options.booking_url && (
        <p className="mb-4 text-xs font-medium uppercase tracking-wide text-slate-400">
          <Trans>Étape 1 sur 2 · Vos informations</Trans>
        </p>
      )}
      <label className="required mb-1 text-primary" htmlFor="acces-statut">
        <Trans>Statut</Trans>
      </label>
      <select
        id="acces-statut"
        className={fieldClass}
        value={statut}
        onChange={(e) => setStatut(e.target.value as Statut)}
        required
      >
        <option value="Médecin">{t`Médecin`}</option>
        <option value="Chirurgien">{t`Chirurgien`}</option>
        <option value="Hôpital">{t`Hôpital`}</option>
        <option value="Société savante">{t`Société savante`}</option>
        <option value="Fabricant de dispositifs médicaux">
          {t`Fabricant de dispositifs médicaux`}
        </option>
        <option value="Industriel de santé">{t`Industriel de santé`}</option>
      </select>

      {/* An interest, optional and multiple: nothing is subscribed to, nothing shared because of it. */}
      <fieldset className="mb-4">
        <legend className="mb-1 text-primary">
          <Trans>Offres qui vous intéressent</Trans>
          <span className="ml-2 text-xs italic text-primary opacity-30">
            <Trans>facultatif</Trans>
          </span>
        </legend>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {OFFERS.map((offer) => {
            const checked = offres.includes(offer.id);
            return (
              <label
                key={offer.id}
                className={`flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 transition-colors ${
                  checked
                    ? "border-primary bg-primary/5"
                    : "border-slate-200 hover:border-primary/50"
                }`}
              >
                <input
                  type="checkbox"
                  name="offres"
                  value={offer.id}
                  className="mt-0.5 rounded border-slate-300 text-primary focus:ring-primary"
                  checked={checked}
                  onChange={(e) => toggleOffre(offer.id, e.target.checked)}
                />
                <span className="flex flex-col">
                  <span className="text-sm font-medium text-slate-900">
                    {offer.name}
                  </span>
                  <span className="text-xs text-slate-500">
                    {t(offer.audience)}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {/* A physician gives a title (Dr, Pr); an institution's or a company's contact has none. */}
      <div
        className={
          isPhysician
            ? "grid grid-cols-[5rem_minmax(0,1fr)_minmax(0,1fr)] gap-3"
            : "grid grid-cols-2 gap-3"
        }
      >
        {isPhysician && (
          <div className="flex flex-col">
            <label className="required mb-1 text-primary" htmlFor="acces-titre">
              <Trans>Titre</Trans>
            </label>
            <select
              id="acces-titre"
              className={fieldClass}
              value={titre}
              onChange={(e) => setTitre(e.target.value as Titre)}
              required
            >
              <option value="Dr">Dr</option>
              <option value="Pr">Pr</option>
            </select>
          </div>
        )}
        <div className="flex flex-col">
          <Input
            id="acces-prenom"
            label={t`Prénom`}
            name="prenom"
            type="text"
            autoComplete="given-name"
            placeholder={t`Ex. : Marie`}
            required
            value={prenom}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPrenom(e.target.value)
            }
          />
        </div>
        <div className="flex flex-col">
          <Input
            id="acces-nom"
            // A context of its own: "Nom" alone is translated "Name" elsewhere, and beside
            // "First name" this one is the family name.
            label={t({ message: "Nom", context: "nom de famille" })}
            name="nom"
            type="text"
            autoComplete="family-name"
            placeholder={t`Ex. : Dupont`}
            required
            value={nom}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setNom(e.target.value)
            }
          />
        </div>
      </div>

      {!isPhysician && (
        <Input
          id="acces-fonction"
          label={t`Fonction`}
          info={t`facultatif`}
          name="fonction"
          type="text"
          autoComplete="organization-title"
          placeholder={
            isIndustry
              ? t`Ex. : Responsable affaires cliniques`
              : isLearnedSociety
                ? t`Ex. : Secrétaire général`
                : t`Ex. : Directeur des systèmes d'information`
          }
          value={fonction}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFonction(e.target.value)
          }
        />
      )}

      <Input
        id="acces-email"
        label={t`Email professionnel`}
        name="email"
        type="email"
        autoComplete="email"
        inputMode="email"
        placeholder={
          isIndustry
            ? t`Ex. : nom@entreprise.com`
            : isLearnedSociety
              ? t`Ex. : nom@societe-savante.fr`
              : t`Ex. : nom@chu.fr`
        }
        required
        customValidity={t`Veuillez entrer un email valide`}
        value={email}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setEmail(e.target.value);
          if (error === "exists") setError(null);
        }}
      />
      {error === "exists" && (
        <p className="-mt-2 mb-4 text-xs text-red-600">
          <Trans>Un compte existe déjà avec cet email.</Trans>{" "}
          {onAccountExists && (
            <button
              type="button"
              onClick={() => onAccountExists(email)}
              className="font-semibold underline underline-offset-2"
            >
              <Trans>Se connecter</Trans>
            </button>
          )}
        </p>
      )}

      <Input
        id="acces-telephone"
        label={t`Téléphone`}
        info={t`facultatif`}
        name="telephone"
        type="tel"
        autoComplete="tel"
        placeholder={t`Ex. : 06 12 34 56 78`}
        value={telephone}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setTelephone(e.target.value)
        }
      />

      <Input
        id="acces-clinique"
        label={
          isIndustry
            ? t`Nom de l'entreprise`
            : isLearnedSociety
              ? t`Nom de la société savante`
              : isPhysician
                ? t`Centre hospitalier, clinique ou entreprise`
                : t`Nom de l'établissement`
        }
        name="clinique"
        type="text"
        autoComplete="organization"
        placeholder={
          isIndustry
            ? t`Ex. : Implants Médicaux SA`
            : isLearnedSociety
              ? t`Ex. : Société française de chirurgie rachidienne`
              : t`Ex. : CHU de Marseille`
        }
        required
        value={clinique}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setClinique(e.target.value)
        }
      />

      {/* Required from a physician; an institution or a company may name the one it is about. */}
      <label
        className={`${isPhysician ? "required " : ""}mb-1 text-primary`}
        htmlFor="acces-specialite"
      >
        {isPhysician ? (
          <Trans>Spécialité</Trans>
        ) : (
          <>
            <Trans>Spécialité concernée</Trans>
            <span className="ml-2 text-xs italic text-primary opacity-30">
              <Trans>facultatif</Trans>
            </span>
          </>
        )}
      </label>
      <select
        id="acces-specialite"
        className={fieldClass}
        value={specialite}
        onChange={(e) => setSpecialite(e.target.value)}
        required={isPhysician}
      >
        <option value="" disabled={isPhysician}>
          {isPhysician ? t`Sélectionner une spécialité` : t`Non précisée`}
        </option>
        {options.specialites.map((sp) => (
          <option key={sp.id} value={sp.id}>
            {specialityLabel(sp.id, t, sp)}
          </option>
        ))}
        <option value={OTHER}>{t`Autre spécialité`}</option>
      </select>

      {asksSoftware && (
        <>
          <label className="mb-1 text-primary" htmlFor="acces-editeur">
            <Trans>Logiciel médical utilisé</Trans>
            <span className="ml-2 text-xs italic text-primary opacity-30">
              <Trans>facultatif</Trans>
            </span>
          </label>
          <select
            id="acces-editeur"
            className={fieldClass}
            value={editeur}
            onChange={(e) => setEditeur(e.target.value)}
          >
            <option value="">{t`Je ne sais pas`}</option>
            {options.editeurs_logiciels.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
            <option value={OTHER}>{t`Autre logiciel`}</option>
          </select>
        </>
      )}

      {/* Honeypot */}
      <input
        type="text"
        name="username"
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
      />

      <label className="flex items-start gap-2 mb-4 text-xs text-slate-600 cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5 rounded border-slate-300 text-primary focus:ring-primary"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          required
        />
        <span>
          <Trans>
            J'accepte que mes données soient utilisées pour traiter ma demande
            d'accès et être recontacté par l'équipe MAGE-X.
          </Trans>{" "}
          <Link
            to="/cgus"
            className="text-primary underline underline-offset-2"
            target="_blank"
            rel="noreferrer"
          >
            <Trans>Conditions générales</Trans>
          </Link>
          {" · "}
          <Link
            to="/confidentialité"
            className="text-primary underline underline-offset-2"
            target="_blank"
            rel="noreferrer"
          >
            <Trans>Politique de confidentialité</Trans>
          </Link>
        </span>
      </label>

      {error === "failed" && (
        <p className="mb-3 text-xs text-red-600">
          <Trans>
            Une erreur s'est produite. Veuillez réessayer ou nous contacter.
          </Trans>
        </p>
      )}

      <button
        className="w-full rounded-lg bg-gradient-to-r from-primary to-primary/90 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:shadow-md transition-all disabled:cursor-not-allowed disabled:opacity-50"
        type="submit"
        disabled={submitting}
      >
        {submitting ? (
          <Trans>Envoi...</Trans>
        ) : options.booking_url ? (
          <Trans>Continuer</Trans>
        ) : (
          <Trans>Demander un accès</Trans>
        )}
      </button>
      <p className="mt-2 text-center text-xs text-slate-400">
        {options.booking_url ? (
          <Trans>
            Étape suivante : réserver votre rendez-vous avec l'équipe
          </Trans>
        ) : (
          <Trans>Votre demande sera examinée par l'équipe MAGE-X</Trans>
        )}
      </p>
    </form>
  );
}

/** The booking page in the app's language — Google reads `hl` on its appointment pages. */
function localizedBookingUrl(url: string, locale: string): string {
  try {
    const localized = new URL(url);
    localized.searchParams.set("hl", locale);
    return localized.toString();
  } catch {
    return url;
  }
}

/**
 * Step 2: the team's Google Calendar booking page, full screen and with no way to dismiss it.
 *
 * The page is Google's, embedded: it tells the parent nothing, so a booking cannot be checked from
 * here. What this step can do is leave no other path than booking — no close button, Escape and a
 * click outside are ignored — and Google confirms the slot inside the frame once it is taken.
 */
function BookingStep({ url }: { url: string }) {
  const { t } = useLingui();
  const { locale } = useLocaleContext();
  const src = localizedBookingUrl(url, locale);
  return (
    <Dialog.Root open>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
        <Dialog.Content
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-white shadow-2xl focus:outline-none sm:inset-6 sm:mx-auto sm:max-w-4xl sm:rounded-2xl"
        >
          <div className="border-b border-slate-100 px-6 py-4">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
              <Trans>Étape 2 sur 2 · Votre rendez-vous</Trans>
            </p>
            <Dialog.Title className="mt-1 text-base font-semibold text-slate-900">
              <Trans>Réservez votre premier échange avec l'équipe MAGE-X</Trans>
            </Dialog.Title>
            <Dialog.Description className="mt-1 text-sm text-slate-600">
              <Trans>
                Votre demande est enregistrée. Choisissez un créneau ci-dessous
                pour la finaliser : votre accès est ouvert à l'issue de ce
                rendez-vous.
              </Trans>
            </Dialog.Description>
          </div>
          <iframe
            src={src}
            title={t`Calendrier de réservation MAGE-X`}
            className="w-full flex-1 border-0"
          />
          <div className="border-t border-slate-100 px-6 py-2 text-right">
            <a
              href={src}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-slate-500 underline underline-offset-2 hover:text-primary transition-colors"
            >
              <Trans>
                Le calendrier ne s'affiche pas ? Ouvrir dans un nouvel onglet
              </Trans>
            </a>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
